import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useDonorAuth } from '../context/DonorAuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Heart, User, Mail, Lock, Phone, MapPin, Loader2, ArrowLeft } from 'lucide-react';

const DonorRegisterPage = () => {
  const { language } = useLanguage();
  const { register } = useDonorAuth();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    address: '',
    city: '',
    postal_code: '',
  });

  const t = {
    fr: {
      title: 'Créer un compte donateur',
      subtitle: 'Accédez à votre historique de dons et vos reçus fiscaux',
      firstName: 'Prénom',
      lastName: 'Nom',
      email: 'Courriel',
      password: 'Mot de passe',
      confirmPassword: 'Confirmer le mot de passe',
      phone: 'Téléphone (optionnel)',
      address: 'Adresse (optionnel)',
      city: 'Ville (optionnel)',
      postalCode: 'Code postal (optionnel)',
      register: 'Créer mon compte',
      haveAccount: 'Vous avez déjà un compte?',
      login: 'Se connecter',
      passwordMismatch: 'Les mots de passe ne correspondent pas',
      passwordTooShort: 'Le mot de passe doit contenir au moins 8 caractères',
      success: 'Compte créé avec succès!',
      backToDonate: 'Retour aux dons',
    },
    en: {
      title: 'Create a Donor Account',
      subtitle: 'Access your donation history and tax receipts',
      firstName: 'First Name',
      lastName: 'Last Name',
      email: 'Email',
      password: 'Password',
      confirmPassword: 'Confirm Password',
      phone: 'Phone (optional)',
      address: 'Address (optional)',
      city: 'City (optional)',
      postalCode: 'Postal Code (optional)',
      register: 'Create Account',
      haveAccount: 'Already have an account?',
      login: 'Log in',
      passwordMismatch: 'Passwords do not match',
      passwordTooShort: 'Password must be at least 8 characters',
      success: 'Account created successfully!',
      backToDonate: 'Back to donations',
    }
  };

  const text = t[language];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error(text.passwordMismatch);
      return;
    }
    
    if (formData.password.length < 8) {
      toast.error(text.passwordTooShort);
      return;
    }

    setIsSubmitting(true);

    const { confirmPassword, ...registerData } = formData;
    const result = await register(registerData);

    if (result.success) {
      toast.success(text.success);
      navigate('/donor/dashboard');
    } else {
      toast.error(result.error);
    }

    setIsSubmitting(false);
  };

  return (
    <div data-testid="donor-register-page" className="min-h-screen bg-gradient-to-b from-[#D4AF37]/10 to-white pt-24 pb-16">
      <div className="max-w-xl mx-auto px-4 sm:px-6">
        {/* Back Link */}
        <Link 
          to="/donate" 
          className="inline-flex items-center text-[#4A708B] hover:text-[#3B5A6F] mb-8"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {text.backToDonate}
        </Link>

        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 mx-auto rounded-full bg-[#D4AF37]/20 flex items-center justify-center mb-4">
            <Heart className="w-8 h-8 text-[#D4AF37]" />
          </div>
          <h1 className="font-serif text-3xl font-bold text-[#1A252C] mb-2">
            {text.title}
          </h1>
          <p className="text-[#546A76]">{text.subtitle}</p>
        </div>

        {/* Form */}
        <div className="bg-white rounded-3xl shadow-lg p-8">
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Name Row */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="first_name">{text.firstName} *</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#546A76]" />
                  <Input
                    id="first_name"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleChange}
                    required
                    data-testid="register-firstname"
                    className="pl-10 rounded-xl border-[#E2E8F0] focus:border-[#4A708B]"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="last_name">{text.lastName} *</Label>
                <Input
                  id="last_name"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleChange}
                  required
                  data-testid="register-lastname"
                  className="rounded-xl border-[#E2E8F0] focus:border-[#4A708B]"
                />
              </div>
            </div>

            {/* Email */}
            <div className="space-y-2">
              <Label htmlFor="email">{text.email} *</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#546A76]" />
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  data-testid="register-email"
                  className="pl-10 rounded-xl border-[#E2E8F0] focus:border-[#4A708B]"
                />
              </div>
            </div>

            {/* Password Row */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="password">{text.password} *</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#546A76]" />
                  <Input
                    id="password"
                    name="password"
                    type="password"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    minLength={8}
                    data-testid="register-password"
                    className="pl-10 rounded-xl border-[#E2E8F0] focus:border-[#4A708B]"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">{text.confirmPassword} *</Label>
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                  data-testid="register-confirm-password"
                  className="rounded-xl border-[#E2E8F0] focus:border-[#4A708B]"
                />
              </div>
            </div>

            {/* Phone */}
            <div className="space-y-2">
              <Label htmlFor="phone">{text.phone}</Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#546A76]" />
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  data-testid="register-phone"
                  className="pl-10 rounded-xl border-[#E2E8F0] focus:border-[#4A708B]"
                />
              </div>
            </div>

            {/* Address */}
            <div className="space-y-2">
              <Label htmlFor="address">{text.address}</Label>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#546A76]" />
                <Input
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  data-testid="register-address"
                  className="pl-10 rounded-xl border-[#E2E8F0] focus:border-[#4A708B]"
                />
              </div>
            </div>

            {/* City and Postal Code */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="city">{text.city}</Label>
                <Input
                  id="city"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  data-testid="register-city"
                  className="rounded-xl border-[#E2E8F0] focus:border-[#4A708B]"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="postal_code">{text.postalCode}</Label>
                <Input
                  id="postal_code"
                  name="postal_code"
                  value={formData.postal_code}
                  onChange={handleChange}
                  data-testid="register-postal"
                  className="rounded-xl border-[#E2E8F0] focus:border-[#4A708B]"
                />
              </div>
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={isSubmitting}
              data-testid="register-submit"
              className="w-full gold-btn rounded-full py-6 text-lg font-medium mt-6"
            >
              {isSubmitting ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                text.register
              )}
            </Button>
          </form>

          {/* Login Link */}
          <p className="text-center text-[#546A76] mt-6">
            {text.haveAccount}{' '}
            <Link to="/donor/login" className="text-[#4A708B] font-medium hover:underline">
              {text.login}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default DonorRegisterPage;
